import { isEqual, memoize, omit } from 'lodash-es'
import { useEffect, useRef } from 'react'
import RouteNotFound from 'router/RouteNotFound'
import { useAccount } from 'state/Account'
import { PRIVILEGES } from 'state/Constants'
import { checkJWT, useBoolState } from 'utils'
import Model from './Model'

export default class User extends Model {

	/** @type number */ id
	/** @type string */ email
	/** @type string */ username
	/** @type string */ display_name
	/** @type Date */ created_at

	/**
	 * "Unreadable" JWT containing the user privileges.
	 * @see _getPrivileges()
	 * @see usePrivilege()
	 * @private
	 * @type string
	 */
	privileges

	/**
	 * Checks if the user has a privilege or not, through a series of intricate hook calls - because React.
	 *
	 * It's NOT possible to avoid a flash of changed content when an UI element depends on a privilege - the privilege check _must_ be done async, and await can't be used in React components (so they won't freeze while stuff runs)...
	 * Thus, in many scenarios you _will_ end up rendering twice, and flashing one of the privilege status. E.g. a  button that starts hidden because the default is to consider unprivileged, and then the button suddenly shows up because the privilege check finally resolved to true.
	 *
	 * See also:
	 * - {@link setupPrivilegesHook()} - where this is actually defined
	 * - {@link isAllowed()} if you're not inside a React component.
	 * @todo see https://youtrack.jetbrains.com/issue/WI-69689
	 *
	 * @type {function(privilege:string|PRIVILEGES_LIST,initial:boolean=false):boolean}
	 * @param {string} privilege Any of the values at {@link PRIVILEGES()}
	 * @param {boolean} initial=false In most cases you should keep the default of false - it's better to show new content than to hide something the user shouldn't have access to. However, in some scenarios it might be preferable to default to true.
	 * @return {boolean} A boolean based in an internal state; it _always_ starts as `false` and then may jump to `true` once the internal promises resolve.
	 */
	usePrivilege

	constructor(attr) {
		super(attr)

		if (attr && 'created_at' in attr) { //might be empty in rare cases (broken state), crashing even safety logouts. Check with isValid
			this.created_at = new Date(attr.created_at) // eslint-disable-line @miovision/disallow-date/no-new-date
		}

		this.usePrivilege = setupPrivilegesHook(this, this.privileges)
	}

	//might be useful if you're questioning if Auth state isn't broken
	get isValid() {
		return !!this.id && !!this.username
	}

	static isEqual(a, b) {
		const blacklist = ['usePrivilege'] //this is a function created during construct, so it will always be different
		return isEqual(omit(a, blacklist), omit(b, blacklist))
	}

	static _decodePrivileges = memoize(async (user, privilegesJWT) => {
		return checkJWT(user, privilegesJWT, { claim: 'prv', forceLogout: true })
	})

	/**
	 * The list of privileges the user has.
	 * @returns {Promise<?string>} Might return null in case privileges were not loaded yet - check for console warnings
	 * @private
	 */
	async _getPrivileges() {
		if (!this.privileges) {
			console.warn('JWT privileges were not loaded yet') //expected during the migration; if it's constant, it IS a bug
			return null
		}

		return this._decodePrivileges(this, this.privileges)
	}

	/**
	 * Non-JSX method to check if the user has a given privilege.
	 * @see {@link User.usePrivilege()} If you need to check permissions directly on the UI.
	 * @param privilege
	 * @returns {Promise<boolean>}
	 */
	async isAllowed(privilege) {
		const list = await this._getPrivileges()
		if (list) {
			return hasPrivilege(list, privilege)
		}
		return false
	}

	/**
	 * Privilege check for whole screens - if that's now allowed, returns a {@link RouteNotFound}.
	 * Remember to use the resulting variable to also block network requests tied to the same privilege!
	 * @example
	 *  const notAllowed = user.useFullPagePrivilege(PRIVILEGES().BILLING_INFO, 'Invoices')
	 * 	useEffectIf(!notAllowed, () => { doSomething() }) //or useDispatchEffectIf()
	 *  if (notAllowed) {
	 *      return notAllowed
	 *  }
	 * @param {string} privilege Any of the values at {@link PRIVILEGES()}
	 * @param {string|object} page something to identify the page. It will be logged somewhat like a 404, but identifiable to the trained eye that this is not a trivial 404 (since it doesn't contain actual `useLocation()` properties).
	 * @returns {JSX.Element}
	 * @todo the initial result here is to consider the user having the privilege, in case the JWT wasn't decoded yet; this means there could be a flash of inaccessible screen (FOIS? and maybe a premature 403 in the API), when in a further re-render, the privileges get updated and the user is verified unprivileged. I couldn't see much of a way to improve this, since the root cause is JWT decoding being async, and the inability to run async code in JWT. The best solution is probably create a Suspense which waits for the JWT to be decoded? Anyway, this is low priority since the user will only see a FOIS if they followed a link or otherwise tried to enter the inaccessible URL manually.
	 */
	useFullPagePrivilege(privilege, page) {
		if (!this.usePrivilege(privilege, true)) {
			return <RouteNotFound location={typeof page == 'object'? page : { page }}/>
		}
		return null
	}

	/**
	 * Checks if the current user+account are allowed to adjust orders.
	 * Optionally, you can pass an order and it would verify if it's still adjustable.
	 * @param order {?Order}
	 * @returns boolean
	 */
	useCanAdjustOrders(order = null) {
		//yet another way to circumvent rules-of-hooks and keep our objects being useful and actionable
		return useCanAdjustOrders.apply(this) && (!order || order.canBeAdjusted())
	}
}

/**
 * An external function - a "real" hook -, which is embedded into {@link User.useCanAdjustOrders()}, so we don't need to be so functional and recreate/pass objects as parameters everywhere.
 * @return boolean
 */
function useCanAdjustOrders() {
	const account = useAccount()
	return !!(this.usePrivilege(PRIVILEGES().ORDER) && !account.adjustments_disabled)
}

/**
 * Yep, seems stupid, but it's useful to centralize the actual privilege check (for debugs) :)
 * @param list {string}
 * @param privilege {string}
 * @returns {boolean}
 */
function hasPrivilege(list, privilege) {
	const allowed = !!list.includes(privilege)
	console.debug(`Checking privilege "${privilege}" at ${list}`, { allowed })
	return allowed
}

/**
 * Creates the necessary shenanigans to gather async information inside JSX code. Call this inside the constructor of the object, passing itself as argument.
 *
 * If you're REALLY interested in understanding this, read along:
 * - JSX can't call async functions, nor components can be async
 * - decryption is, obviously, an asynchronous call, because JS
 * - thus, to decrypt the JWT we _need_ an async function
 * - to get the ball rolling, we call an async function inside a normal useEffect function (as suggested by useEffect itself, since effects must be sync)
 * - then, since that all is... well, async, we need to store the value somewhere for it to change later - that goes into a {@link BoolState}. We can't depend on promises since this would complicate the usage when waiting for a simple boolean.
 * - once the privileges get loaded, the effect runs again since the list of privileges is a dependency of it
 * - finally, the JWT is unwrapped, privilege checked, final state set, and it's all rendered
 * - Bonus: to save up some performance, we go with useRef() to "cache" the clear list of privileges
 * @param privilegesJWT {string} The privileges JWT string.
 * @note Apparently, while trying to shove those hooks into the Model, I ended up getting a hook-calling function which doesn't need to start with "use"??? The resulting function doesn't need "use" either, but to keep it meaningful, it's nice if you still use "use" lol
 * @todo try to use Suspense to avoid all this mess and have actual async code in JWT (and a loading spinner, of course). This raw promise should be used during the initial load, so there's no FOIC, and then any other privilege check depending on a revisit of this promise should display a small spinner on the corner, instead of fully blocking the screen - this is also important for when the privileges get reloaded.
 */
const setupPrivilegesHook = memoize((user, privilegesJWT) => {
	return (privilege, initial = false) => {
		//startup references - will keep their state in further calls
		const isAllowed  = useBoolState(initial)
		const jwtRef     = useRef(privilegesJWT)
		const privileges = useRef(null)

		//this loads the privilege and stores in the boolean state, returning that.
		//it's not directly affected by other privileges being requested (that's another function call), nor by an updated
		// JWT string - the main hook memoizes based on the JWT string, so if it changes, this function body is re-made,
		// updating the JWT ref from above and refreshing the decoded privileges from the other ref
		useEffect(() => {
			(async () => {
				if (privilegesJWT && (jwtRef.current != privilegesJWT || !privileges.current)) {
					jwtRef.current     = privilegesJWT //allows us to update the JWT string when it's changed in the state
					privileges.current = await User._decodePrivileges(user, jwtRef.current) //if changed or empty, decode the JWT
				}
				return privileges.current? isAllowed.toggle(hasPrivilege(privileges.current, privilege)) : initial
			})()
		}, [isAllowed, privilege, initial])

		return isAllowed.is
	}
})
